import { Button, Flex, Form } from "antd";
import { useEffect } from "react";
import type { AutoApprovalThreshold } from "@/common/model";
import { CustomDrawer } from "@/components/layout/workspace/custom-drawer";
import { AutomationPolicyField } from "./automation-policy-ladder";

/**
 * Drawer for changing one environment's automation policy. The policy is the only thing in here on
 * purpose: it needs the ladder beside it to be legible, which an inline select in a details row has
 * no room for.
 */
export function AutomationPolicyDrawer({
	open,
	onClose,
	current,
	onSave,
}: {
	open: boolean;
	onClose: () => void;
	current: AutoApprovalThreshold;
	onSave: (value: AutoApprovalThreshold) => void;
}) {
	const [form] = Form.useForm();

	// Reopening after a save elsewhere must show the stored value, not the last edit.
	useEffect(() => {
		if (open) form.setFieldsValue({ autoApproveUpTo: current });
	}, [open, current, form]);

	const submit = () => {
		form.validateFields().then((vals) => {
			const next = vals.autoApproveUpTo as AutoApprovalThreshold;
			if (next !== current) onSave(next);
			onClose();
		});
	};

	return (
		<CustomDrawer
			title="Automation policy"
			open={open}
			onClose={onClose}
			footer={
				<Flex gap={8} justify="flex-end">
					<Button onClick={onClose}>Cancel</Button>
					<Button type="primary" onClick={submit}>
						Save
					</Button>
				</Flex>
			}
		>
			<Form form={form} layout="vertical">
				<AutomationPolicyField initialValue={current} />
			</Form>
		</CustomDrawer>
	);
}
